import { staticAssetsApi } from "@api/static-assets";
import { createSelector } from "@reduxjs/toolkit";
import {
  getAccount,
  getSubscriptionPaymentPreset,
  getSubscriptionPaymentPresetOverride,
  getSubscriptionPlan,
} from "@store/account/selectors";
import get from "lodash.get";
import merge from "lodash.merge";
import { SUBSCRIPTION_LIMITS } from "@/shared/constants";
import {
  type FeatureResult,
  PLAN_HIERARCHY,
  type PlanPresets,
  type TPlan,
} from "./types";

const subscriptionLimits =
  staticAssetsApi.endpoints.getSubscriptionLimits.select({});

export const getSubscriptionLimits = createSelector(
  subscriptionLimits,
  (limits) => limits?.data || {},
);

export const getCurrentSubscriptionPreset = createSelector(
  getSubscriptionPaymentPreset,
  getSubscriptionPaymentPresetOverride,
  getSubscriptionLimits,
  (accountPaymentPreset, accountPaymentPresetOverride, limits) => {
    const current = Object.keys(limits).find((limit) => limits[limit].current);

    const preset = limits[accountPaymentPreset] || limits[current || ""];
    const override = accountPaymentPresetOverride || {};
    const baseLimits = JSON.parse(JSON.stringify(SUBSCRIPTION_LIMITS));

    if (preset) {
      const plans = PLAN_HIERARCHY.slice();

      for (let i = plans.length - 1; i >= 0; i--) {
        const currentPlan = plans[i];
        const currentPlanData = preset[currentPlan];

        if (!currentPlanData) continue;

        const booleanFeatures = findBooleanFeatures(currentPlanData);

        for (let j = 0; j < i; j++) {
          const lowerPlan = plans[j];
          if (preset[lowerPlan]) {
            booleanFeatures.forEach((feature) => {
              removeFeatureFromObject(preset[lowerPlan], feature);
            });
          }

          if (baseLimits[lowerPlan]) {
            booleanFeatures.forEach((feature) => {
              removeFeatureFromObject(baseLimits[lowerPlan], feature);
            });
          }
        }
      }
    }

    if (override && Object.keys(override).length > 0) {
      const plans = PLAN_HIERARCHY.slice();

      for (let i = plans.length - 1; i >= 0; i--) {
        const currentPlan = plans[i];
        const currentPlanData = override[currentPlan];

        if (!currentPlanData) continue;

        const booleanFeatures = findBooleanFeatures(currentPlanData);

        for (let j = 0; j < i; j++) {
          const lowerPlan = plans[j];
          if (override[lowerPlan]) {
            booleanFeatures.forEach((feature) => {
              removeFeatureFromObject(override[lowerPlan], feature);
            });
          }
        }
      }
    }

    return merge({}, baseLimits, preset || {}, override);
  },
);

const findBooleanFeatures = (
  obj: Record<string, unknown>,
  path = "",
  results: string[] = [],
): string[] => {
  if (!obj || typeof obj !== "object") return results;

  Object.entries(obj).forEach(([key, value]) => {
    const currentPath = path ? `${path}.${key}` : key;

    if (typeof value === "boolean") {
      results.push(currentPath);
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      findBooleanFeatures(
        value as Record<string, unknown>,
        currentPath,
        results,
      );
    }
  });

  return results;
};

const removeFeatureFromObject = (
  obj: Record<string, unknown>,
  path: string,
): void => {
  const parts = path.split(".");
  const lastPart = parts.pop();

  if (!lastPart) return;

  let current = obj;

  for (const part of parts) {
    if (
      current[part] === undefined ||
      current[part] === null ||
      typeof current[part] !== "object"
    ) {
      return;
    }
    current = current[part] as Record<string, unknown>;
  }

  if (current && current[lastPart] !== undefined) {
    delete current[lastPart];
  }
};

export const getPresetFeature = <T>(feature: string) =>
  createSelector(
    getCurrentSubscriptionPreset,
    getAccount,
    getSubscriptionPlan,
    (preset, account, subscriptionPlan) => {
      if (!account) return { value: null, plan: "admin" };
      let currentPlan: TPlan = "trial";
      if (account.plan === "paid" || account.plan === undefined) {
        const planTitle = subscriptionPlan?.title?.toLowerCase();
        if (!account.isSelfServ) currentPlan = "team";
        else if (planTitle === "pro") currentPlan = "pro";
        else if (planTitle === "starter") currentPlan = "starter";
        else currentPlan = "pro";
      }

      return getFeatureFromPreset(preset, feature, currentPlan) as {
        value: T;
        plan: TPlan;
      };
    },
  );

/**
 * Gets a feature from the preset based on the user's current plan.
 * Checks from one level above current plan up to the top tier.
 * Continues to check higher plans if a feature value is null or undefined.
 */
const getFeatureFromPreset = (
  preset: PlanPresets,
  feature: string,
  currentPlan: TPlan,
): { value: FeatureResult; plan: TPlan } => {
  // Find current plan index in hierarchy
  const currentPlanIndex = PLAN_HIERARCHY.indexOf(currentPlan);

  // Check from one level above current plan, moving upward
  for (let i = currentPlanIndex; i < PLAN_HIERARCHY.length; i++) {
    const planToCheck = PLAN_HIERARCHY[i];
    const planPreset = preset[planToCheck as keyof PlanPresets];

    // Get the feature value
    const featureValue = get(planPreset, feature, null);

    // Skip null/undefined values and continue to next plan
    if (featureValue === null || featureValue === undefined) {
      continue;
    }

    if (typeof featureValue === "number") {
      return { value: featureValue, plan: PLAN_HIERARCHY[i + 1] || "team" };
    }

    if (typeof featureValue === "boolean" && currentPlan === planToCheck) {
      return { value: null, plan: PLAN_HIERARCHY[i] };
    }

    return { value: featureValue, plan: PLAN_HIERARCHY[i] };
  }

  // If no valid value is found in any higher plan, return null with current plan
  return { value: null, plan: currentPlan };
};
